import { EnvironmentName } from "../environment/environment.interface";
import {
  ensureReadableColor,
  getContrastingTextColor,
  hexToRgb,
  isColorLight,
} from "../util/color";
import {
  CORNER_RADIUS_TOKENS,
  CornerRadiusEnum,
  getCornerRadiusValues,
} from "./corner-radius";
import {
  mergeThemeOverrides,
  pickDivisionThemeOverrides,
  ThemeOverrideFields,
} from "./theme-tokens";

export type ThemeMode = "light" | "dark";
export type ThemeVariableMap = Record<string, string>;

const GRAY = {
  0: "#fff",
  50: "#fcfcfe",
  100: "#f5f6fa",
  250: "#e5e7eb",
  500: "#8f9297",
  750: "#363a3f",
  800: "#292c31",
  850: "#1f2125",
  900: "#141518",
  950: "#121316",
};

// Brand defaults used when a division/brandkit hasn't supplied its own.
// Positive mirrors the builder's `$color-positive` so the two surfaces
// agree at a glance.
const FEEDBACK_POSITIVE = "#00A775";
const FEEDBACK_NEGATIVE = "#e02640";

/**
 * The baseline link blue, before contrast correction — deliberately not the
 * brand accent: "blue, underlined" is what a reader recognises as a link, and
 * an accent picked to work as a button fill often doesn't read as text at all.
 * Two starting points because a correction can only move a color toward black
 * or white, and each mode starts on the right side of its own background. The
 * dark one is emitted as `--color-text-link-on-dark` as well, for surfaces
 * that only learn they are dark at cascade time.
 */
const LINK_BLUE: Record<ThemeMode, string> = {
  light: "#0b5fd4",
  dark: "#6aa8ff",
};

export interface EnvironmentTheme {
  primary: string;
  primaryDark: string;
  fontPrimary: string;
  fontSecondary: string;
  cornerRadius: CornerRadiusEnum;
  feedbackPositive?: string;
  feedbackNegative?: string;
}

const INTRACTIVE_THEME: EnvironmentTheme = {
  primary: "#2e68fc",
  primaryDark: "#346dff",
  fontPrimary: `"Lato", "Segoe UI", Tahoma, sans-serif`,
  fontSecondary: `"Poppins", "Segoe UI", Tahoma, sans-serif`,
  cornerRadius: CornerRadiusEnum.Medium,
};

const ENERGYPLATFORM_THEME: EnvironmentTheme = {
  primary: "#f27249",
  primaryDark: "#f27249",
  fontPrimary: `"Inter", sans-serif`,
  fontSecondary: `"Inter", sans-serif`,
  cornerRadius: CornerRadiusEnum.Small,
  feedbackPositive: "#003b42",
};

export const ENVIRONMENT_THEMES: Record<EnvironmentName, EnvironmentTheme> = {
  intractive: INTRACTIVE_THEME,
  citychallenge: INTRACTIVE_THEME,
  energyplatform: ENERGYPLATFORM_THEME,
  energywalks: ENERGYPLATFORM_THEME,
};

export interface ThemeResolveInput {
  environment: EnvironmentName;
  mode: ThemeMode;
  contributions?: Partial<ThemeOverrideFields>;
  fontPrimary?: string;
  fontSecondary?: string;
  cornerRadius?: CornerRadiusEnum | string;
}

export interface ThemeBlockInput extends ThemeResolveInput {
  selector: string;
}

/**
 * Typed JS view of the resolved theme. Returned by `resolveThemeColors`
 * and exposed to React via `ThemeContext` so JS-driven UI (3D meshes,
 * charts, canvas helpers) reads the same palette the CSS variables do.
 */
export interface ThemeColors {
  colorPrimary: string;
  colorText: string;
  colorHeading: string;
  colorBackground: string;
  colorSurface: string;
  colorSurfaceOpacity: number;
  colorBorder: string;
  /** Stroke weight in px-equivalent units; XR/3D consumers scale as needed. */
  borderWidth: number;
  colorButtonPrimary: string;
  colorButtonText: string;
  colorFeedbackPositive: string;
  /** Contrast-inferred text color for content on a feedback-positive bg. */
  colorFeedbackPositiveText: string;
  colorFeedbackNegative: string;
  /** Contrast-inferred text color for content on a feedback-negative bg. */
  colorFeedbackNegativeText: string;
}

interface ResolvedTheme extends ThemeColors {
  colorBase: string;
  colorBaseInvert: string;
  colorBackgroundAlt: string;
  colorCard: string;
  colorElement: string;
  colorElevated: string;
  colorPopover: string;
  colorTextMuted: string;
  colorTextLink: string;
  colorTextError: string;
  colorButton: string;
  colorProgress: string;
  colorBorderOpacity: number;
  colorButtonPrimaryOpacity: number;
  colorButtonTextOpacity: number;
  fontPrimary: string;
  fontSecondary: string;
  primaryIsLight: boolean;
  cornerRadius?: CornerRadiusEnum | string;
  balloonArrowEnabled?: boolean;
}

function setColor(
  out: ThemeVariableMap,
  baseVar: string,
  value: string | undefined,
  fallback: string
) {
  const resolved = value || fallback;
  out[baseVar] = resolved;
  const rgb = hexToRgb(resolved);
  if (rgb) out[`${baseVar}-rgb`] = rgb;
}

/**
 * Strip CSS-isms that the resolver can't honor as a color value (notably
 * `"transparent"`, which is truthy but not a parseable hex/rgb string and
 * blows up `new THREE.Color()` on the XR side). Treated as "unset" so the
 * cascade falls through to the next layer.
 */
function color(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value.toLowerCase() === "transparent") return undefined;
  return value;
}

/**
 * Internal: walks contributions + env defaults and produces a typed JS
 * object covering every resolved field. Both `resolveThemeColors` (the
 * public typed projection) and `resolveThemeVariables` (the CSS variable
 * emitter) read from this so they can't drift apart.
 */
function resolveTheme(input: ThemeResolveInput): ResolvedTheme {
  const env = ENVIRONMENT_THEMES[input.environment];
  const c = input.contributions || {};
  const isLight = input.mode === "light";

  // Mode-isolated contributions: in dark mode the `*DarkMode` field wins
  // over the light-mode field, never the other way round. Brandkit blocks
  // always render in light mode.
  const colorPrimary = isLight
    ? color(c.color) || env.primary
    : color(c.colorPrimaryDarkMode) || color(c.color) || env.primaryDark;
  const colorText = isLight
    ? color(c.colorText) || GRAY[900]
    : color(c.colorTextDarkMode) || GRAY[0];

  const colorButtonPrimary = color(c.colorButtonPrimary) || colorPrimary;
  // Button text auto-infers from button primary unless explicitly set.
  // Same pattern is reused below for feedback-positive/negative text.
  const colorButtonText =
    color(c.colorButtonText) ||
    getContrastingTextColor(colorButtonPrimary) ||
    "#FFFFFF";

  // A single colorSurface override seeds surface/card/element/elevated/popover.
  const surfaceDefault = isLight ? GRAY[50] : GRAY[850];
  const cardDefault = isLight ? GRAY[0] : GRAY[800];
  const elevatedDefault = isLight ? GRAY[100] : GRAY[800];
  const popoverDefault = isLight ? GRAY[0] : GRAY[800];
  const surfaceOverride = color(c.colorSurface);
  const colorSurface = surfaceOverride || surfaceDefault;

  const colorBackground =
    color(c.colorBackground) || (isLight ? GRAY[0] : GRAY[950]);

  const colorFeedbackPositive =
    color(c.feedbackColorPositive) || env.feedbackPositive || FEEDBACK_POSITIVE;
  const colorFeedbackNegative =
    color(c.feedbackColorNegative) || env.feedbackNegative || FEEDBACK_NEGATIVE;

  return {
    colorPrimary,
    colorText,
    colorHeading: color(c.colorHeading) || colorText,
    colorBackground,
    colorSurface,
    colorSurfaceOpacity: c.colorSurfaceOpacity ?? 0.6,
    colorBorder: color(c.colorBorder) || (isLight ? GRAY[250] : GRAY[750]),
    borderWidth: 1,
    colorButtonPrimary,
    colorButtonText,
    colorFeedbackPositive,
    colorFeedbackNegative,

    colorBase: isLight ? GRAY[0] : GRAY[950],
    colorBaseInvert: isLight ? GRAY[950] : GRAY[0],
    colorBackgroundAlt: isLight ? GRAY[100] : GRAY[950],
    colorCard: surfaceOverride || cardDefault,
    colorElement: surfaceOverride || surfaceDefault,
    colorElevated: surfaceOverride || elevatedDefault,
    colorPopover: surfaceOverride || popoverDefault,
    colorTextMuted: GRAY[500],
    // Fixed hue, enforced legibility on both surfaces a link lands on: page
    // blocks paint `colorSurface` (text and collection cards), while story
    // markdown sits on the background. Surface first — that is where the body
    // copy carrying links actually lives.
    colorTextLink:
      ensureReadableColor(LINK_BLUE[input.mode], [
        colorSurface,
        colorBackground,
      ]) || LINK_BLUE[input.mode],
    colorTextError: FEEDBACK_NEGATIVE,
    colorButton: isLight ? GRAY[0] : GRAY[800],
    colorFeedbackPositiveText:
      getContrastingTextColor(colorFeedbackPositive) || "#FFFFFF",
    colorFeedbackNegativeText:
      getContrastingTextColor(colorFeedbackNegative) || "#FFFFFF",
    colorProgress: color(c.colorProgress) || "#fff",
    colorBorderOpacity: c.colorBorderOpacity ?? 1,
    colorButtonPrimaryOpacity: c.colorButtonPrimaryOpacity ?? 1,
    colorButtonTextOpacity: c.colorButtonTextOpacity ?? 1,
    fontPrimary: input.fontPrimary || env.fontPrimary,
    fontSecondary: input.fontSecondary || env.fontSecondary,
    primaryIsLight: isColorLight(colorPrimary),
    cornerRadius: input.cornerRadius,
    balloonArrowEnabled: c.balloonArrowEnabled,
  };
}

/**
 * Public typed projection of the resolver — same data the CSS variables
 * carry, but as a plain JS object for `ThemeContext` and any JS-driven UI
 * (3D, canvas, chart libraries). Pair with `resolveDivisionThemeColors`
 * when you have a division/brandkit pair.
 */
export function resolveThemeColors(input: ThemeResolveInput): ThemeColors {
  const t = resolveTheme(input);
  return {
    colorPrimary: t.colorPrimary,
    colorText: t.colorText,
    colorHeading: t.colorHeading,
    colorBackground: t.colorBackground,
    colorSurface: t.colorSurface,
    colorSurfaceOpacity: t.colorSurfaceOpacity,
    colorBorder: t.colorBorder,
    borderWidth: t.borderWidth,
    colorButtonPrimary: t.colorButtonPrimary,
    colorButtonText: t.colorButtonText,
    colorFeedbackPositive: t.colorFeedbackPositive,
    colorFeedbackPositiveText: t.colorFeedbackPositiveText,
    colorFeedbackNegative: t.colorFeedbackNegative,
    colorFeedbackNegativeText: t.colorFeedbackNegativeText,
  };
}

export interface DivisionThemeResolveInput {
  environment: EnvironmentName;
  mode?: ThemeMode;
  /** Raw division entity — only its theme-override fields are read. */
  division?: Partial<ThemeOverrideFields>;
  /**
   * Optional active brandkit. Merged on top of the division's
   * contributions, mirroring `render.ts`'s SSR pipeline.
   */
  brandkit?: Partial<ThemeOverrideFields>;
}

/**
 * Convenience over `resolveThemeColors`: applies the same
 * division-then-brandkit cascade that SSR uses. Use this from JS contexts
 * (e.g. `useElementSlideTheme`, the builder's scene preview) so the JS
 * theme stays in lockstep with the emitted CSS variables.
 */
export function resolveDivisionThemeColors(
  input: DivisionThemeResolveInput
): ThemeColors {
  const divisionContribs = input.division
    ? pickDivisionThemeOverrides(input.division)
    : {};
  const contributions = input.brandkit
    ? mergeThemeOverrides(divisionContribs, input.brandkit)
    : divisionContribs;
  return resolveThemeColors({
    environment: input.environment,
    mode: input.mode || "light",
    contributions,
  });
}

export function resolveThemeVariables(
  input: ThemeResolveInput
): ThemeVariableMap {
  const t = resolveTheme(input);
  const out: ThemeVariableMap = {};

  setColor(out, "--color-base", undefined, t.colorBase);
  setColor(out, "--color-base-invert", undefined, t.colorBaseInvert);

  setColor(out, "--color-background", t.colorBackground, t.colorBackground);
  setColor(out, "--color-background-alt", undefined, t.colorBackgroundAlt);

  setColor(out, "--color-primary", t.colorPrimary, t.colorPrimary);

  setColor(out, "--color-surface", t.colorSurface, t.colorSurface);
  setColor(out, "--color-card", t.colorCard, t.colorCard);
  setColor(out, "--color-element", t.colorElement, t.colorElement);
  setColor(out, "--color-elevated", t.colorElevated, t.colorElevated);
  setColor(out, "--color-popover", t.colorPopover, t.colorPopover);

  setColor(out, "--color-border", t.colorBorder, t.colorBorder);

  setColor(out, "--color-text", t.colorText, t.colorText);
  out["--color-text-dimmed"] = `rgba(var(--color-text-rgb), 0.8)`;
  out["--color-text-dimmed-rgb"] = out["--color-text-rgb"];
  // Muted is derived from the text color like dimmed, but a touch more faded.
  out["--color-text-muted"] = `rgba(var(--color-text-rgb), 0.66)`;
  out["--color-text-muted-rgb"] = out["--color-text-rgb"];
  setColor(out, "--color-text-link", undefined, t.colorTextLink);
  // Uncorrected, and always the dark-mode blue: this is what a surface picks up
  // when it discovers in CSS that it is dark (page-blocks' block backgrounds),
  // where the page-level correction went the other way.
  setColor(out, "--color-text-link-on-dark", undefined, LINK_BLUE.dark);
  setColor(out, "--color-text-error", undefined, t.colorTextError);

  setColor(out, "--color-heading", t.colorHeading, t.colorHeading);

  setColor(out, "--color-button", undefined, t.colorButton);
  setColor(
    out,
    "--color-button-primary",
    t.colorButtonPrimary,
    t.colorButtonPrimary
  );
  setColor(out, "--color-button-text", t.colorButtonText, t.colorButtonText);

  setColor(
    out,
    "--color-feedback-positive",
    t.colorFeedbackPositive,
    t.colorFeedbackPositive
  );
  setColor(
    out,
    "--color-feedback-negative",
    t.colorFeedbackNegative,
    t.colorFeedbackNegative
  );
  setColor(
    out,
    "--color-feedback-positive-text",
    t.colorFeedbackPositiveText,
    t.colorFeedbackPositiveText
  );
  setColor(
    out,
    "--color-feedback-negative-text",
    t.colorFeedbackNegativeText,
    t.colorFeedbackNegativeText
  );

  setColor(out, "--color-progress", t.colorProgress, t.colorProgress);

  // Surfaces default to slightly translucent; the rest are opaque unless
  // the brandkit explicitly dials them down.
  out["--color-surface-opacity"] = String(t.colorSurfaceOpacity);
  out["--color-border-opacity"] = String(t.colorBorderOpacity);
  out["--color-button-primary-opacity"] = String(t.colorButtonPrimaryOpacity);
  out["--color-button-text-opacity"] = String(t.colorButtonTextOpacity);

  out["--border-width"] = `${t.borderWidth}px`;

  out["--primary-is-light"] = t.primaryIsLight ? "1" : "0";

  // Balloon arrow visibility. Unset/true keeps the arrow; only an explicit
  // `false` hides it, so existing themes are unaffected.
  out["--balloon-arrow-display"] =
    t.balloonArrowEnabled === false ? "none" : "block";

  out["--font-primary"] = t.fontPrimary;
  out["--font-secondary"] = t.fontSecondary;

  // Only a theme that sets a radius itself re-declares the tokens; the rest
  // inherit from the page root (`emitCornerRadiusRoot`), so a block themed
  // with the built-in dark theme or a radius-less brandkit keeps the page's
  // corners rather than snapping back to the environment default.
  const radii = getCornerRadiusValues(t.cornerRadius);
  if (radii) Object.assign(out, radii);

  return out;
}

/**
 * The document-level `--border-radius-*` tokens: the division's radius, or the
 * environment default. Emit once alongside the theme blocks; themes without
 * a radius of their own inherit from here.
 */
export function emitCornerRadiusRoot(
  environment: EnvironmentName,
  cornerRadius?: CornerRadiusEnum | string
): string {
  // `default` and anything unrecognised resolve to no values, which is what
  // makes them "unset" — fall through to the environment's radius rather than
  // emitting an empty rule and leaving the tokens undefined.
  const radii =
    getCornerRadiusValues(cornerRadius) ??
    getCornerRadiusValues(ENVIRONMENT_THEMES[environment].cornerRadius);
  const body = Object.entries(radii ?? {})
    .map(([name, value]) => `${name}: ${value};`)
    .join("");
  return `:root{${body}}`;
}

/**
 * The radius tokens set to `inherit`, for a scope that declares no radius of
 * its own yet has to beat an earlier rule that did. Runtime blocks stay sparse
 * and inherit by omission; only a live preview layered over a saved block
 * (the brandkit edit modal) needs to say so explicitly.
 */
export function emitCornerRadiusInherit(selector: string): string {
  const body = CORNER_RADIUS_TOKENS.map((name) => `${name}: inherit;`).join("");
  return `${selector}{${body}}`;
}

export function emitThemeStyleBlock(blocks: ThemeBlockInput[]): string {
  return blocks
    .map((block) => {
      const variables = resolveThemeVariables(block);
      const body = Object.entries(variables)
        .map(([name, value]) => `${name}: ${value};`)
        .join("");
      return `${block.selector}{${body}}`;
    })
    .join("");
}
